import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { predefinedLinesAPI } from '../services/api';

const getTypeColor = (type) => {
  switch (type) {
    case 'installation':
      return 'primary';
    case 'subscription':
      return 'success';
    case 'service':
      return 'info';
    case 'maintenance':
      return 'warning';
    case 'equipment':
      return 'secondary';
    case 'product':
      return 'default';
    case 'discount':
      return 'error';
    case 'note':
      return 'info';
    case 'other':
      return 'default';
    default:
      return 'default';
  }
};

const getTypeLabel = (type) => {
  switch (type) {
    case 'installation':
      return 'Installation';
    case 'subscription':
      return 'Abonnement';
    case 'service':
      return 'Service';
    case 'maintenance':
      return 'Maintenance';
    case 'equipment':
      return 'Équipement';
    case 'product':
      return 'Produit';
    case 'discount':
      return 'Remise';
    case 'note':
      return 'Note';
    case 'other':
      return 'Autre';
    default:
      return type;
  }
};

function PredefinedLinesSelector({ open, onClose, onSelectLine }) {
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (open) {
      loadPredefinedLines();
    }
  }, [open]);

  const loadPredefinedLines = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await predefinedLinesAPI.getAll();
      // Handle paginated response
      const linesData = response.data.results || response.data;
      setLines(linesData);
    } catch (err) {
      console.error('Error loading predefined lines:', err);
      setError('Erreur lors du chargement des lignes prédéfinies');
    } finally {
      setLoading(false);
    }
  };

  const filteredLines = (Array.isArray(lines) ? lines : []).filter(line =>
    line.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (line.reference && line.reference.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleSelectLine = (line) => {
    // Convert predefined line to the format expected by the modals
    const lineItem = {
      description: line.description,
      line_type: line.line_type,
      quantity: 1,
      months: 1,
      unit_price_ht: parseFloat(line.pu_ht) || 0,
    };

    onSelectLine(lineItem);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ m: 0, p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Sélectionner une ligne prédéfinie</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ m: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <Box sx={{ p: 2 }}>
              <TextField
                fullWidth
                placeholder="Rechercher par description ou référence..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Description</strong></TableCell>
                    <TableCell><strong>Type</strong></TableCell>
                    <TableCell align="right"><strong>Prix U. HT</strong></TableCell>
                    <TableCell><strong>Référence</strong></TableCell>
                    <TableCell align="center"><strong>Action</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredLines.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography color="text.secondary">
                          {searchTerm ? 'Aucune ligne trouvée' : 'Aucune ligne prédéfinie disponible'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredLines.map((line) => (
                      <TableRow key={line.id} hover>
                        <TableCell>{line.description}</TableCell>
                        <TableCell>
                          <Chip
                            label={getTypeLabel(line.line_type)}
                            color={getTypeColor(line.line_type)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          {parseFloat(line.pu_ht).toLocaleString('fr-FR')} DZD
                        </TableCell>
                        <TableCell>{line.reference || '-'}</TableCell>
                        <TableCell align="center">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={() => handleSelectLine(line)}
                          >
                            Ajouter
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Fermer</Button>
      </DialogActions>
    </Dialog>
  );
}

export default PredefinedLinesSelector;
